import React from 'react'

const Dashboard = React.lazy(() => import('./views/dashboard/Dashboard'))
const Colors = React.lazy(() => import('./views/theme/colors/Colors'))
const Typography = React.lazy(() => import('./views/theme/typography/Typography'))

// Users
const Users = React.lazy(() => import('./views/users/Users'))
const User = React.lazy(() => import('./views/users/User'))

// Courses
const Courses = React.lazy(() => import('./views/courses/Courses'))
const CourseForm = React.lazy(() => import('./views/courses/CourseForm'))

// Student Groups
const Groups = React.lazy(() => import('./views/groups/Groups'))
const GroupForm = React.lazy(() => import('./views/groups/GroupForm'))
const GroupScheduleManagement = React.lazy(() => import('./views/groups/GroupScheduleManagement'))
const MyGroups = React.lazy(() => import('./views/groups/MyGroups'))
const MyGroupDetails = React.lazy(() => import('./views/groups/MyGroupDetails'))

// Lectures
const Lectures = React.lazy(() => import('./views/lectures/Lectures'))
const Lecture = React.lazy(() => import('./views/lectures/Lecture'))
const ProfessorLectures = React.lazy(() => import('./views/lectures/ProfessorLectures'))

// Attendance
const Attendance = React.lazy(() => import('./views/attendance/Attendance'))

// Homework
const Homework = React.lazy(() => import('./views/homework/Homework'))
const HomeworkAssignment = React.lazy(() => import('./views/homework/HomeworkAssignment'))

// Payments
const Payments = React.lazy(() => import('./views/payments/Payments'))
const PaymentsDue = React.lazy(() => import('./views/payments/PaymentsDue'))
const PaymentsManagement = React.lazy(() => import('./views/payments/PaymentsManagement'))
const MyPayments = React.lazy(() => import('./views/payments/MyPayments'))
const ParentPayments = React.lazy(() => import('./views/payments/ParentPayments'))
const PaymentConfiguration = React.lazy(() => import('./views/payments/PaymentConfiguration'))

// Discounts
const Discounts = React.lazy(() => import('./views/discounts/Discounts'))

// Reports
const Reports = React.lazy(() => import('./views/reports/Reports'))

// Settings
const Settings = React.lazy(() => import('./views/settings/Settings'))
const JobMonitor = React.lazy(() => import('./views/settings/JobMonitor'))

// Admin
const RoleManagement = React.lazy(() => import('./views/admin/RoleManagement'))
const StudentGroupTypes = React.lazy(() => import('./views/admin/StudentGroupTypes'))
const ViewsManagement = React.lazy(() => import('./views/admin/ViewsManagement'))
const RolePermissionsManagement = React.lazy(() => import('./views/admin/RolePermissionsManagement'))
const TenantSettings = React.lazy(() => import('./views/admin/TenantSettings'))
const TenantMemberManagement = React.lazy(() => import('./views/admin/TenantMemberManagement'))
const AuditLogViewer = React.lazy(() => import('./views/admin/AuditLogViewer'))
const TenantAnalyticsDashboard = React.lazy(() => import('./views/admin/TenantAnalyticsDashboard'))
const SubscriptionManagement = React.lazy(() => import('./views/admin/SubscriptionManagement'))

// Platform Admin
const PlatformAdminDashboard = React.lazy(() => import('./views/platform-admin/PlatformAdminDashboard'))
const PendingTenants = React.lazy(() => import('./views/platform-admin/PendingTenants'))
const PaymentProvidersManagement = React.lazy(() => import('./views/platform-admin/PaymentProvidersManagement'))
const SubscriptionRequests = React.lazy(() => import('./views/platform-admin/SubscriptionRequests'))

// Parents
const ParentCrossTenantDashboard = React.lazy(() => import('./views/parents/ParentCrossTenantDashboard'))

// Rooms
const Rooms = React.lazy(() => import('./views/rooms/Rooms'))
const RoomForm = React.lazy(() => import('./views/rooms/RoomForm'))

// Profile
const Profile = React.lazy(() => import('./views/profile/Profile'))

// Projects
const Projects = React.lazy(() => import('./views/projects/Projects'))

// Account
const LockAccount = React.lazy(() => import('./views/account/LockAccount'))

const routes = [
  { path: '/', exact: true, name: 'Home' },
  { 
    path: '/dashboard', 
    name: 'Dashboard', 
    element: Dashboard,
    permission: { resource: 'dashboard', action: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor', 'student', 'parent']
  },
  { path: '/theme', name: 'Theme', element: Colors, exact: true },
  { path: '/theme/colors', name: 'Colors', element: Colors },
  { path: '/theme/typography', name: 'Typography', element: Typography },
  
  // Users routes
  { 
    path: '/users', 
    name: 'Users', 
    element: Users, 
    exact: true,
    permission: { viewCode: 'users', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  { 
    path: '/users/:id', 
    name: 'User Details', 
    element: User,
    permission: { viewCode: 'users.detail', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  { 
    path: '/students', 
    name: 'Students', 
    element: Users,
    permission: { viewCode: 'users', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor']
  },
  { 
    path: '/professors', 
    name: 'Professors', 
    element: Users,
    permission: { viewCode: 'users', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  { 
    path: '/parents', 
    name: 'Parents', 
    element: Users,
    permission: { viewCode: 'users', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  
  // Courses routes
  { 
    path: '/courses', 
    name: 'Courses', 
    element: Courses, 
    exact: true,
    permission: { viewCode: 'courses', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  { 
    path: '/courses/create', 
    name: 'Create Course', 
    element: CourseForm,
    permission: { viewCode: 'courses', actionCode: 'create' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  { 
    path: '/courses/:id', 
    name: 'Edit Course', 
    element: CourseForm,
    permission: { viewCode: 'courses.detail', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  
  // Student Groups routes
  { 
    path: '/groups', 
    name: 'Student Groups', 
    element: Groups, 
    exact: true,
    permission: { viewCode: 'groups', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor']
  },
  { 
    path: '/groups/create', 
    name: 'Create Group', 
    element: GroupForm,
    permission: { viewCode: 'groups', actionCode: 'create' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  { 
    path: '/groups/:id/schedule', 
    name: 'Group Schedule', 
    element: GroupScheduleManagement,
    permission: { viewCode: 'groups.detail', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  { 
    path: '/groups/:id', 
    name: 'Edit Group', 
    element: GroupForm,
    permission: { viewCode: 'groups.detail', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  
  // My Groups routes (for students)
  { 
    path: '/my-groups', 
    name: 'My Groups', 
    element: MyGroups, 
    exact: true,
    permission: { resource: 'myGroups', action: 'view' },
    allowedRoles: ['student']
  },
  { 
    path: '/my-groups/:id', 
    name: 'My Group Details', 
    element: MyGroupDetails,
    permission: { resource: 'myGroups.detail', action: 'view' },
    allowedRoles: ['student']
  },
  
  // Lectures routes
  { 
    path: '/lectures', 
    name: 'Lectures', 
    element: Lectures, 
    exact: true,
    permission: { resource: 'lectures', action: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor', 'student']
  },
  { 
    path: '/lectures/my', 
    name: 'My Lectures', 
    element: ProfessorLectures,
    permission: { resource: 'lectures', action: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor']
  },
  { 
    path: '/lectures/calendar', 
    name: 'Lecture Calendar', 
    element: Lectures,
    permission: { resource: 'lectures', action: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor', 'student']
  },
  { 
    path: '/lectures/:id', 
    name: 'Lecture Details', 
    element: Lecture,
    permission: { resource: 'lectures', action: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor', 'student']
  },
  { 
    path: '/lectures/schedule', 
    name: 'Schedule Lecture', 
    element: Lecture,
    permission: { resource: 'lectures', action: 'create' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor']
  },
  
  // Attendance routes
  { 
    path: '/attendance', 
    name: 'Attendance', 
    element: Attendance,
    permission: { resource: 'attendance', action: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor', 'student', 'parent']
  },
  { 
    path: '/attendance/mark', 
    name: 'Mark Attendance', 
    element: Attendance,
    permission: { resource: 'attendance', action: 'mark' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor']
  },
  { 
    path: '/attendance/reports', 
    name: 'Attendance Reports', 
    element: Attendance,
    permission: { resource: 'attendance', action: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor']
  },
  { 
    path: '/attendance/my', 
    name: 'My Attendance', 
    element: Attendance,
    permission: { resource: 'attendance', action: 'view' },
    allowedRoles: ['student', 'parent']
  },
  
  // Homework routes
  { 
    path: '/homework', 
    name: 'Homework', 
    element: Homework, 
    exact: true,
    permission: { viewCode: 'homework', actionCode: 'list' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor', 'student']
  },
  { 
    path: '/homework/create', 
    name: 'Create Assignment', 
    element: HomeworkAssignment,
    permission: { viewCode: 'homework', actionCode: 'create' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor']
  },
  { 
    path: '/homework/submissions', 
    name: 'Submissions', 
    element: Homework,
    permission: { viewCode: 'homework', actionCode: 'list' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor']
  },
  { 
    path: '/homework/:id', 
    name: 'Assignment Details', 
    element: HomeworkAssignment,
    permission: { viewCode: 'homework.detail', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor', 'student']
  },
  
  // Payments routes
  { 
    path: '/payments', 
    name: 'Payments', 
    element: Payments, 
    exact: true,
    permission: { viewCode: 'payments', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  { 
    path: '/payments/configuration', 
    name: 'Payment Configuration', 
    element: PaymentConfiguration,
    permission: { viewCode: 'payment-configuration', actionCode: 'list' },
    allowedRoles: ['super-admin', 'ceo', 'admin']
  },
  { 
    path: '/payments/my', 
    name: 'My Payments', 
    element: MyPayments,
    permission: { viewCode: 'payments.my', actionCode: 'view' },
    allowedRoles: ['student']
  },
  { 
    path: '/payments/children', 
    name: 'Children Payments', 
    element: ParentPayments,
    permission: { viewCode: 'payments.children', actionCode: 'view' },
    allowedRoles: ['parent']
  },
  { 
    path: '/payments/management', 
    name: 'Payments Management', 
    element: PaymentsManagement,
    permission: { viewCode: 'payments.management', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'admin']
  },
  { 
    path: '/payments/due', 
    name: 'Payments Due', 
    element: PaymentsDue,
    permission: { viewCode: 'payments', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'student', 'parent']
  },
  { 
    path: '/payments/history', 
    name: 'Payment History', 
    element: Payments,
    permission: { viewCode: 'payments', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'student', 'parent']
  },
  
  // Discounts route (RBAC managed from database)
  { 
    path: '/discounts', 
    name: 'Discounts', 
    element: Discounts
  },
  
  // Reports routes
  { 
    path: '/reports', 
    name: 'Reports', 
    element: Reports, 
    exact: true,
    permission: { resource: 'reports', action: 'list' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  { 
    path: '/reports/academic', 
    name: 'Academic Reports', 
    element: Reports,
    permission: { resource: 'reports', action: 'read' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  { 
    path: '/reports/attendance', 
    name: 'Attendance Reports', 
    element: Reports,
    permission: { resource: 'reports', action: 'read' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  { 
    path: '/reports/performance', 
    name: 'Performance Reports', 
    element: Reports,
    permission: { resource: 'reports', action: 'read' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  { 
    path: '/reports/financial', 
    name: 'Financial Reports', 
    element: Reports,
    permission: { resource: 'reports', action: 'read' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  
  // Analytics
  { 
    path: '/analytics', 
    name: 'Analytics', 
    element: Reports,
    permission: { resource: 'reports', action: 'read' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  
  // Communication
  { 
    path: '/notifications', 
    name: 'Notifications', 
    element: Dashboard 
  },
  { 
    path: '/messages', 
    name: 'Messages', 
    element: Dashboard 
  },
  
  // Settings
  { 
    path: '/settings', 
    name: 'Settings', 
    element: Settings 
  },
  { 
    path: '/settings/jobs', 
    name: 'Job Monitor', 
    element: JobMonitor,
    permission: { resource: 'settings.jobs', action: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'admin', 'coordinator']
  },
  
  // Admin routes
  { 
    path: '/admin/roles', 
    name: 'Role Management', 
    element: RoleManagement,
    permission: { viewCode: 'admin.roles', actionCode: 'view' },
    allowedRoles: ['super-admin']
  },
  { 
    path: '/admin/views', 
    name: 'Views Management', 
    element: ViewsManagement,
    permission: { viewCode: 'admin.views', actionCode: 'view' },
    allowedRoles: ['super-admin']
  },
  { 
    path: '/admin/permissions', 
    name: 'Role Permissions', 
    element: RolePermissionsManagement,
    permission: { viewCode: 'admin.permissions', actionCode: 'view' }
  },
  { 
    path: '/admin/group-types', 
    name: 'Group Types', 
    element: StudentGroupTypes,
    permission: { resource: 'groups', action: 'read' },
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  { 
    path: '/admin/tenant-settings', 
    name: 'Academy Settings', 
    element: TenantSettings,
    permission: { viewCode: 'my-academy', actionCode: 'view' },
    allowedRoles: ['super-admin', 'ceo', 'admin']
  },
  { 
    path: '/admin/tenant-members', 
    name: 'Member Management', 
    element: TenantMemberManagement,
    permission: { resource: 'tenants', action: 'update' },
    allowedRoles: ['super-admin', 'ceo', 'admin']
  },
  { 
    path: '/admin/audit-log', 
    name: 'Audit Log', 
    element: AuditLogViewer,
    permission: { resource: 'audit-log', action: 'read' },
    allowedRoles: ['super-admin', 'ceo', 'admin']
  },
  { 
    path: '/admin/analytics', 
    name: 'Analytics Dashboard', 
    element: TenantAnalyticsDashboard,
    permission: { resource: 'analytics', action: 'read' },
    allowedRoles: ['super-admin', 'ceo', 'admin', 'coordinator']
  },
  { 
    path: '/admin/subscription', 
    name: 'Subscription Management', 
    element: SubscriptionManagement,
    permission: { resource: 'tenants', action: 'read' },
    allowedRoles: ['super-admin', 'ceo', 'admin']
  },
  { 
    path: '/admin/rooms', 
    name: 'Room Management', 
    element: Rooms,
    exact: true,
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  { 
    path: '/rooms/add', 
    name: 'Add Room', 
    element: RoomForm,
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  { 
    path: '/rooms/:id', 
    name: 'Edit Room', 
    element: RoomForm,
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  
  // Platform Admin routes
  { 
    path: '/platform-admin/dashboard', 
    name: 'Platform Administration', 
    element: PlatformAdminDashboard,
    permission: { resource: 'platform', action: 'admin' },
    allowedRoles: ['platform-admin'],
    minHierarchyLevel: 100
  },
  { 
    path: '/platform-admin/pending', 
    name: 'Pending Verifications', 
    element: PendingTenants,
    permission: { resource: 'platform', action: 'admin' },
    allowedRoles: ['platform-admin'],
    minHierarchyLevel: 100
  },
  { 
    path: '/platform-admin/payment-providers', 
    name: 'Payment Providers', 
    element: PaymentProvidersManagement,
    permission: { resource: 'platform', action: 'admin' },
    allowedRoles: ['platform-admin'],
    minHierarchyLevel: 100
  },
  { 
    path: '/platform-admin/subscription-requests', 
    name: 'Subscription Requests', 
    element: SubscriptionRequests,
    permission: { resource: 'platform', action: 'admin' },
    allowedRoles: ['platform-admin'],
    minHierarchyLevel: 100
  },
  
  // Parent routes
  { 
    path: '/parent/children', 
    name: 'My Children', 
    element: ParentCrossTenantDashboard,
    permission: { resource: 'children', action: 'view' },
    allowedRoles: ['parent']
  },
  
  // Profile
  { 
    path: '/profile', 
    name: 'Profile', 
    element: Profile,
    allowedRoles: ['super-admin', 'ceo', 'coordinator', 'professor', 'student', 'parent']
  },
  
  // Projects
  { 
    path: '/projects', 
    name: 'Projects', 
    element: Projects,
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
  
  // Lock Account
  { 
    path: '/lock-account', 
    name: 'Lock Account', 
    element: LockAccount,
    allowedRoles: ['super-admin', 'ceo', 'coordinator']
  },
]

export default routes